// Arranging a wall in the editor (D-067): drag posts to new places, like app icons on a phone.
// The post being dragged lifts and follows the pointer; the others glide out of its way (FLIP:
// measure, move in the page, then animate from the old place to the new one). Only the editor
// loads this file; walls on brands' websites never do.

const EASE = "cubic-bezier(.2,.8,.2,1)";

const STYLE = `
[data-id]{touch-action:none;cursor:grab;user-select:none;-webkit-user-select:none}
[data-id] img{pointer-events:none;-webkit-user-drag:none}
.lifted{position:relative;z-index:10;cursor:grabbing}
.lifted .tile{transform:scale(1.06);box-shadow:0 18px 40px rgba(0,0,0,.28);transition:transform .18s ${EASE},box-shadow .18s}
.arranging [data-id]:not(.lifted) .tile{animation:bw-jiggle .26s ease-in-out infinite alternate}
.arranging [data-id]:nth-child(2n) .tile{animation-delay:-.13s}
@keyframes bw-jiggle{from{transform:rotate(-.7deg)}to{transform:rotate(.7deg)}}
@media (prefers-reduced-motion:reduce){.arranging [data-id] .tile{animation:none}.lifted .tile{transition:none}}
`;

export function arrange(
  root: ShadowRoot,
  o: {
    /** A post was dropped at a new place (0 is first). */
    onMove: (postId: string, index: number) => void;
    /** A post was tapped without dragging. */
    onTap: (postId: string, item: HTMLElement) => void;
  },
) {
  const list = root.querySelector<HTMLElement>(".grid, .masonry, .row");
  if (!list) return;
  const style = document.createElement("style");
  style.textContent = STYLE;
  root.append(style);

  const items = () => Array.from(list.querySelectorAll<HTMLElement>(":scope > [data-id]"));
  /** Where an element sits in the page, without any animation or drag offset. */
  const settled = (el: HTMLElement) => {
    el.style.transition = "none";
    el.style.transform = "";
    return el.getBoundingClientRect();
  };

  let s: {
    item: HTMLElement;
    id: string;
    x: number;
    y: number;
    start: DOMRect;
    from: number;
    dragging: boolean;
  } | null = null;

  list.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    const item = (e.target as Element).closest<HTMLElement>("[data-id]");
    if (!item || !list.contains(item)) return;
    s = {
      item,
      id: item.dataset.id ?? "",
      x: e.clientX,
      y: e.clientY,
      start: item.getBoundingClientRect(),
      from: items().indexOf(item),
      dragging: false,
    };
    list.setPointerCapture(e.pointerId);
  });

  list.addEventListener("pointermove", (e) => {
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!s.dragging) {
      if (Math.hypot(dx, dy) < 6) return;
      s.dragging = true;
      s.item.classList.add("lifted");
      list.classList.add("arranging");
    }
    // The post under the pointer, looking through the one being dragged.
    s.item.style.pointerEvents = "none";
    const under = root.elementFromPoint(e.clientX, e.clientY);
    s.item.style.pointerEvents = "";
    const target = under?.closest<HTMLElement>("[data-id]");
    if (target && target !== s.item && list.contains(target)) {
      const all = items().filter((el) => el !== s?.item);
      const first = new Map(all.map((el) => [el, el.getBoundingClientRect()]));
      const before = items().indexOf(target) < items().indexOf(s.item);
      if (before) target.before(s.item);
      else target.after(s.item);
      for (const el of all) {
        const a = first.get(el);
        const b = settled(el);
        if (!a || (a.left === b.left && a.top === b.top)) continue;
        el.style.transform = `translate(${a.left - b.left}px,${a.top - b.top}px)`;
        el.getBoundingClientRect();
        el.style.transition = `transform .28s ${EASE}`;
        el.style.transform = "";
      }
    }
    // Keep the dragged post under the pointer, wherever its place in the wall is now.
    const now = settled(s.item);
    s.item.style.transform = `translate(${s.start.left + dx - now.left}px,${s.start.top + dy - now.top}px)`;
  });

  const end = (cancelled: boolean) => {
    if (!s) return;
    const done = s;
    s = null;
    if (!done.dragging) {
      if (!cancelled) o.onTap(done.id, done.item);
      return;
    }
    list.classList.remove("arranging");
    // Settle into the new place.
    done.item.style.transition = `transform .24s ${EASE}`;
    done.item.style.transform = "";
    window.setTimeout(() => {
      done.item.classList.remove("lifted");
      done.item.style.transition = "";
    }, 260);
    const to = items().indexOf(done.item);
    if (!cancelled && to !== done.from) o.onMove(done.id, to);
  };
  list.addEventListener("pointerup", () => end(false));
  list.addEventListener("pointercancel", () => end(true));
  // Pointer taps are handled above; keyboard presses (detail 0) still reach the post.
  list.addEventListener(
    "click",
    (e) => {
      if (e.detail !== 0) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}
